import { Injectable } from '@angular/core';
import { BaseEquipmentService } from './base-equipment.service';
import {
  SolarInverter,
  CreateInverterDto,
  UpdateInverterDto,
  EquipmentListResponse,
} from '@features/catalog/models';

/**
 * Inverter Service
 * Handles all inverter-related API operations
 * Endpoint: /organizations/{orgId}/equipment/inverters
 */
@Injectable({
  providedIn: 'root',
})
export class InverterService extends BaseEquipmentService<
  SolarInverter,
  CreateInverterDto,
  UpdateInverterDto
> {
  private readonly equipmentType = 'inverters' as const;

  /**
   * Get all inverters for an organization
   */
  async getInverters(
    organizationId: string
  ): Promise<EquipmentListResponse<SolarInverter>> {
    return this.getAll(organizationId, this.equipmentType);
  }

  /**
   * Get a specific inverter by ID
   */
  async getInverterById(
    organizationId: string,
    inverterId: string
  ): Promise<SolarInverter> {
    return this.getById(organizationId, this.equipmentType, inverterId);
  }

  /**
   * Create a new inverter
   */
  async createInverter(
    organizationId: string,
    data: CreateInverterDto
  ): Promise<SolarInverter> {
    return this.create(organizationId, this.equipmentType, data);
  }

  /**
   * Update an existing inverter
   */
  async updateInverter(
    organizationId: string,
    inverterId: string,
    data: UpdateInverterDto
  ): Promise<SolarInverter> {
    return this.update(organizationId, this.equipmentType, inverterId, data);
  }

  /**
   * Delete an inverter (soft delete)
   */
  async deleteInverter(
    organizationId: string,
    inverterId: string
  ): Promise<void> {
    return this.delete(organizationId, this.equipmentType, inverterId);
  }

  /**
   * Sync global inverters from Solar Insight
   */
  async syncGlobalInverters(organizationId: string): Promise<SolarInverter[]> {
    return this.sync<SolarInverter[]>(organizationId, this.equipmentType);
  }
}
